import { FC, useCallback, useMemo } from "react";
import { SettingPageWrapper } from "@connectedComponents";
import { NavigationPathsEnum } from "@utilities/enums";
import { Button, SimpleImageUploader, Card, Input, InputWrapper } from "@components";
import { useControllers } from "./useControllers";
import imagePlaceholder from '../../../assets/images/img-placeholder.png';
import { COUNTRIES } from "@utilities/constants";
import { getCountryWithCode } from "@utilities/functions";

export const MyOrganisationSettings: FC = () => {
    const {
        organisation, isEditing, isValid,
        isLoadingUpdatedOrganisation,
        isOrganisationInfosIsComplete,
        organisationName, setOrganisationName,
        organisationCity, setOrganisationCity,
        organidationCountry, setOrganisationCountry,
        toggleEdit, handleLogoChange,
        handleUpdateOrganisation,
    } = useControllers();

    const countriesOptions = useMemo(()=> (
        COUNTRIES.map((country)=> ({
            label: country.name,
            value: country.code,
        }))
    ), []);

    const citiesOptions = useMemo(()=> {
        if(!organidationCountry) return [];
        const country = COUNTRIES.find((country)=> country.code === organidationCountry);
        if(!country) return [];
        return country.cities.map((city)=> ({
            label: city,
            value: city,
        }));
    }, [organidationCountry]);

    const renderInformations = useCallback(() => {
        if (!organisation) return null;

        return (
            <div className={`flex-1 ${isEditing ? "hidden" : ""}`}>
                <Card
                    title={
                        organisation.organisationName
                            ? organisation.organisationName
                            : "Infromations de l'organisation"
                    }
                >
                    <div className="flex items-center space-x-4">
                        <div className="w-28 h-28 rounded-full overflow-hidden bg-gray-200">
                            <img
                                src={organisation.logo?.filename || imagePlaceholder}
                                alt="Organisation Logo"
                                className="object-cover w-full h-full"
                            />
                        </div>

                    </div>
                    <div className="mt-4">
                        {
                            organisation.city && (
                                <p>
                                    <strong>Ville: </strong> {organisation.city}
                                </p>
                            )
                        }
                        {
                            organisation.country && (
                                <p>
                                    <strong>Pays: </strong> {getCountryWithCode(organisation.country)}
                                </p>
                            )
                        }
                        <p className="text-sm text-gray-500 mt-2">
                            Crée le: {
                                organisation.createAt
                                    ? new Date(organisation.createAt).toLocaleDateString()
                                    : 'N/A'
                            }
                        </p>
                    </div>
                    {
                        !isEditing && (
                            <div className="mt-4">
                                <Button onClick={toggleEdit} className="btn btn-secondary btn-outline">
                                    Modifier
                                </Button>
                            </div>
                        )
                    }
                </Card>
            </div>
        );
    }, [toggleEdit, isEditing, organisation]);

    const renderForm = useCallback(() => {
        if (!isEditing || !organisation) return null;

        return (
            <div className="flex-1">
                <Card title="Modification des informations">
                    <div
                        className='grid grid-cols-1 md:grid-cols-[1fr_2fr] 
                        lg:grid-cols-[2fr_2fr] gap-6 items-start'
                    >
                        <InputWrapper label="Nom de l'organisation">
                            <Input
                                value={organisationName}
                                onChange={(e) => setOrganisationName(e.target.value)}
                                placeholder="ex: Mon Entreprise"
                            />
                        </InputWrapper>
                        
                        <InputWrapper label="Pays">
                            <Input
                                type="select"
                                placeholder="Selectionner un pays"
                                options={countriesOptions}
                                value={organidationCountry}
                                onChange={(e) => setOrganisationCountry(e.target.value)}
                            />
                        </InputWrapper>

                        <InputWrapper label="Ville">
                            <Input
                                type="select"
                                placeholder="Selectionner une ville"
                                disabled={!organidationCountry}
                                options={citiesOptions}
                                value={organisationCity}
                                onChange={(e) => setOrganisationCity(e.target.value)}
                            />
                        </InputWrapper>
                    </div>

                    <div className="space-y-4 mt-6">
                        <h3 className="text-md font-medium">Logo</h3>
                        <SimpleImageUploader
                            defaultLogo={organisation.logo?.filename!}
                            onChangeLogo={handleLogoChange}
                        />
                    </div>

                    <div className="col-span-full flex justify-end space-x-2">
                        {
                            isOrganisationInfosIsComplete && (
                                <Button
                                    disabled={isLoadingUpdatedOrganisation}
                                    className="btn btn-secondary"
                                    onClick={toggleEdit}
                                >
                                    Annuler
                                </Button>
                            )
                        }
                        <Button
                            disabled={!isValid || isLoadingUpdatedOrganisation}
                            className="btn btn-success"
                            onClick={handleUpdateOrganisation}
                        >
                            {isLoadingUpdatedOrganisation ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }, [
        citiesOptions, countriesOptions,
        isEditing, organisation, organisationName, 
        organidationCountry, organisationCity, isValid,
        isLoadingUpdatedOrganisation, isOrganisationInfosIsComplete,
        handleLogoChange, toggleEdit, handleUpdateOrganisation, 
        setOrganisationName, setOrganisationCountry,
        setOrganisationCity,
    ]);

    return (
        <SettingPageWrapper activeTab={NavigationPathsEnum.SETTINGS_MY_ORGANISATION}>
            {
                !organisation ? (
                    <div>Chargement organisation...</div>
                ) : (
                    <div className="max-w-6xl mx-auto">
                        <h1 className="text-xl md:text-3xl font-bold mb-6">Paramètres de l'organisation</h1>
                        <div className="flex flex-col lg:flex-row lg:bg-none">
                            {renderInformations()}
                            {renderForm()}
                        </div>
                    </div>
                )
            }
        </SettingPageWrapper>
    );
};
